import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { TrendingUp, Mail, Lock, User, ArrowRight, Eye, EyeOff, Check, Gift, Building2, Sparkles, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { register, updateUser } = useAuth();
  const navigate = useNavigate();

  // Business Owner Question Flow
  const [showBusinessQuestion, setShowBusinessQuestion] = useState(false);
  const [isBusinessOwner, setIsBusinessOwner] = useState(null);
  const [wantsToApply, setWantsToApply] = useState(null);

  // Business Owner Modal State
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [businessOwnerInfo, setBusinessOwnerInfo] = useState(null);
  const [bankingStep, setBankingStep] = useState(false);
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_holder_name: '',
    routing_number: '',
    account_number: '',
    account_type: 'checking'
  });
  const [savingBank, setSavingBank] = useState(false);

  // Business Application Modal
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    business_type: '',
    description: '',
    website: '',
    annual_revenue: '',
    investment_amount: ''
  });
  const [submittingApplication, setSubmittingApplication] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Show business owner question first
    setShowBusinessQuestion(true);
  };

  const proceedWithRegistration = async () => {
    setLoading(true);
    
    try {
      const result = await register(email, password, name, referralCode);
      toast.success('Account created successfully!');
      
      // Check if user is a business owner (by email match)
      if (result.business_owner_info && result.business_owner_info.is_business_owner) {
        setBusinessOwnerInfo(result.business_owner_info);
        setBankForm(prev => ({ ...prev, account_holder_name: name }));
        setShowBusinessQuestion(false);
        setShowOwnerModal(true);
      } else if (wantsToApply) {
        // User wants to submit business application
        setShowBusinessQuestion(false);
        setApplicationForm(prev => ({ ...prev, owner_name: name, email: email }));
        setShowApplicationModal(true);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessQuestionNo = () => {
    setIsBusinessOwner(false);
    setWantsToApply(false);
    proceedWithRegistration();
  };

  const handleBusinessQuestionYes = () => {
    setIsBusinessOwner(true);
  };

  const handleWantsToApplyNo = () => {
    setWantsToApply(false);
    proceedWithRegistration();
  };

  const handleWantsToApplyYes = () => {
    setWantsToApply(true);
    proceedWithRegistration();
  };

  const handleSubmitApplication = async () => {
    // Validate required fields
    if (!applicationForm.business_name || applicationForm.business_name.length < 2) {
      toast.error('Business name is required');
      return;
    }
    if (!applicationForm.phone || applicationForm.phone.length < 10) {
      toast.error('Valid phone number is required');
      return;
    }
    if (!applicationForm.business_type) {
      toast.error('Business type is required');
      return;
    }
    if (!applicationForm.description || applicationForm.description.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    if (!applicationForm.investment_amount || parseFloat(applicationForm.investment_amount) < 100) {
      toast.error('Investment amount must be at least $100');
      return;
    }

    setSubmittingApplication(true);
    try {
      await api.post('/business-application/submit', {
        ...applicationForm,
        investment_amount: parseFloat(applicationForm.investment_amount)
      });
      toast.success('Business application submitted! We will review and get back to you.');
      setShowApplicationModal(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmittingApplication(false);
    }
  };

  const handleClaimBusiness = () => {
    setBankingStep(true);
  };

  const handleSkipClaim = () => {
    setShowOwnerModal(false);
    navigate('/dashboard');
  };

  const handleSetupBanking = async () => {
    if (!bankForm.bank_name || !bankForm.routing_number || !bankForm.account_number || !bankForm.account_holder_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (bankForm.routing_number.length !== 9) {
      toast.error('Routing number must be 9 digits');
      return;
    }

    if (bankForm.account_number.length < 8) {
      toast.error('Account number must be at least 8 digits');
      return;
    }

    setSavingBank(true);
    try {
      await api.post('/owner/banking', bankForm);
      toast.success(`Banking setup complete for ${businessOwnerInfo.business_name}!`);
      setShowOwnerModal(false);
      navigate('/business-owner');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup banking');
    } finally {
      setSavingBank(false);
    }
  };

  const handleSkipBanking = () => {
    setShowOwnerModal(false);
    toast.success(`Welcome, owner of ${businessOwnerInfo.business_name}!`);
    navigate('/business-owner');
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1647067517631-aa36cda58a17?crop=entropy&cs=srgb&fm=jpg&w=1200')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0A] to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Start with just $5
          </h2>
          <p className="text-gray-300 text-lg">
            Trade stocks, crypto, precious metals, and support local businesses — all from one platform.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#00E676] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-black" />
            </div>
            <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sraivest
            </span>
          </Link>

          {/* Header */}
          <div>
            <h1 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Create your account
            </h1>
            <p className="text-gray-400 mt-2">
              Join investors backing local businesses growing their wealth
            </p>
          </div>

          {/* Google Signup */}
          <Button 
            onClick={handleGoogleSignup}
            variant="outline"
            className="w-full h-12 bg-white/5 border-white/10 hover:bg-white/10 text-white"
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0A0A0A] text-gray-500">or register with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="input-dark pl-10"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-dark pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-dark pl-10 pr-10"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Must be at least 6 characters</p>
            </div>

            {/* Referral Code */}
            <div className="space-y-2">
              <Label htmlFor="referral" className="text-gray-300">Referral Code (Optional)</Label>
              <div className="relative">
                <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="referral"
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="SRAI..."
                  className="input-dark pl-10"
                  data-testid="referral-input"
                />
              </div>
              {referralCode && (
                <p className="text-xs text-[#00E676]">You'll get 3 trades with 20% fee discount!</p>
              )}
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => setAgreedToTerms(!agreedToTerms)}
                className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                  agreedToTerms 
                    ? 'bg-[#00E676] border-[#00E676]' 
                    : 'border-white/20 hover:border-white/40'
                }`}
                data-testid="terms-checkbox"
              >
                {agreedToTerms && <Check className="w-3 h-3 text-black" />}
              </button>
              <p className="text-sm text-gray-400">
                I agree to the{' '}
                <button type="button" onClick={() => navigate('/terms')} className="text-[#00E676] hover:underline">Terms of Service</button>
                {' '}and{' '}
                <button type="button" onClick={() => { window.location.href = '/#fees'; }} className="text-[#00E676] hover:underline">Fee Structure</button>
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Login link */}
          <p className="text-center text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#00E676] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Business Owner Claim Modal */}
      <Dialog open={showOwnerModal} onOpenChange={setShowOwnerModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {bankingStep ? (
                <>
                  <CreditCard className="w-5 h-5 text-[#00E676]" />
                  Setup Banking Information
                </>
              ) : (
                <>
                  <Building2 className="w-5 h-5 text-[#F7931A]" />
                  Welcome, Business Owner!
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {!bankingStep ? (
            <div className="space-y-6 py-4">
              {/* Business Card */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-[#F7931A]/20 to-[#00E676]/20 border border-[#F7931A]/30">
                <div className="flex items-center gap-4">
                  {businessOwnerInfo?.business_logo && (
                    <img 
                      src={businessOwnerInfo.business_logo} 
                      alt={businessOwnerInfo.business_name}
                      className="w-16 h-16 rounded-xl object-cover border border-white/10"
                    />
                  )}
                  <div>
                    <p className="text-sm text-gray-400">You are registered as the owner of</p>
                    <p className="text-xl font-bold text-white">{businessOwnerInfo?.business_name}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                  <Sparkles className="w-5 h-5 text-[#00E676] mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Receive 90% of Investments</p>
                    <p className="text-sm text-gray-400">When users invest in your business, you receive 90% directly.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                  <CreditCard className="w-5 h-5 text-[#F7931A] mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Setup Your Bank Account</p>
                    <p className="text-sm text-gray-400">Link your bank to receive earnings from your business.</p>
                  </div>
                </div>
              </div>

              <p className="text-center text-gray-400 text-sm">
                Would you like to claim your business and setup banking now?
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-[#00E676]/10 border border-[#00E676]/20">
                <p className="text-sm text-[#00E676] flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Setting up banking for <strong>{businessOwnerInfo?.business_name}</strong>
                </p>
              </div>

              <div className="space-y-2">
                <Label>Bank Name *</Label>
                <Input 
                  value={bankForm.bank_name} 
                  onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})} 
                  placeholder="Chase, Wells Fargo, etc."
                  className="input-dark"
                  data-testid="owner-bank-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Account Holder Name *</Label>
                <Input 
                  value={bankForm.account_holder_name} 
                  onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})} 
                  placeholder="Your full name"
                  className="input-dark"
                  data-testid="owner-holder-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Routing Number * (9 digits)</Label>
                <Input 
                  value={bankForm.routing_number} 
                  onChange={(e) => setBankForm({...bankForm, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9)})} 
                  placeholder="123456789"
                  className="input-dark font-mono"
                  maxLength={9}
                  data-testid="owner-routing"
                />
              </div>

              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input 
                  value={bankForm.account_number} 
                  onChange={(e) => setBankForm({...bankForm, account_number: e.target.value.replace(/\D/g, '').slice(0, 17)})} 
                  placeholder="1234567890"
                  className="input-dark font-mono"
                  type="password"
                  data-testid="owner-account"
                />
              </div>

              <div className="space-y-2">
                <Label>Account Type</Label>
                <select 
                  value={bankForm.account_type}
                  onChange={(e) => setBankForm({...bankForm, account_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white"
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-3">
            {!bankingStep ? (
              <>
                <Button variant="outline" onClick={handleSkipClaim} className="btn-secondary" data-testid="skip-claim-btn">
                  Skip for Now
                </Button>
                <Button onClick={handleClaimBusiness} className="btn-primary" data-testid="claim-business-btn">
                  <Building2 className="w-4 h-4 mr-2" />
                  Yes, Claim My Business
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleSkipBanking} className="btn-secondary" data-testid="skip-banking-btn">
                  Setup Later
                </Button>
                <Button onClick={handleSetupBanking} disabled={savingBank} className="btn-primary" data-testid="save-owner-bank-btn">
                  {savingBank ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  {savingBank ? 'Saving...' : 'Complete Setup'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business Owner Question Modal */}
      <Dialog open={showBusinessQuestion} onOpenChange={setShowBusinessQuestion}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Building2 className="w-5 h-5 text-[#F7931A]" />
              One Quick Question
            </DialogTitle>
          </DialogHeader>
          
          {isBusinessOwner === null ? (
            <div className="space-y-6 py-4">
              <p className="text-gray-300 text-center text-lg">
                Do you own a small business?
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={handleBusinessQuestionNo} 
                  className="flex-1 btn-secondary h-12"
                  disabled={loading}
                  data-testid="business-question-no"
                >
                  No
                </Button>
                <Button 
                  onClick={handleBusinessQuestionYes} 
                  className="flex-1 btn-primary h-12"
                  data-testid="business-question-yes"
                >
                  Yes
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              <p className="text-gray-300 text-center text-lg">
                Would you like to fill out a business application?
              </p>
              <p className="text-sm text-gray-400 text-center">
                List your business on Sraivest and receive investments from our community.
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={handleWantsToApplyNo} 
                  className="flex-1 btn-secondary h-12"
                  disabled={loading}
                  data-testid="apply-question-no"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : 'Not Now'}
                </Button>
                <Button 
                  onClick={handleWantsToApplyYes} 
                  className="flex-1 btn-primary h-12"
                  disabled={loading}
                  data-testid="apply-question-yes"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : 'Yes, Apply'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Business Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Building2 className="w-5 h-5 text-[#00E676]" />
              Business Application
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input 
                  value={applicationForm.business_name}
                  onChange={(e) => setApplicationForm({...applicationForm, business_name: e.target.value})}
                  placeholder="Your Business Name"
                  className="input-dark"
                  data-testid="app-business-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Owner Name *</Label>
                <Input 
                  value={applicationForm.owner_name}
                  onChange={(e) => setApplicationForm({...applicationForm, owner_name: e.target.value})}
                  placeholder="Your Full Name"
                  className="input-dark"
                  data-testid="app-owner-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={applicationForm.email}
                  onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                  placeholder="email@example.com"
                  className="input-dark"
                  data-testid="app-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input 
                  value={applicationForm.phone}
                  onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  placeholder="1234567890"
                  className="input-dark"
                  data-testid="app-phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Type *</Label>
                <select
                  value={applicationForm.business_type}
                  onChange={(e) => setApplicationForm({...applicationForm, business_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white"
                  data-testid="app-business-type"
                >
                  <option value="">Select Type</option>
                  <option value="Food & Beverage">Food & Beverage</option>
                  <option value="Retail">Retail</option>
                  <option value="Services">Services</option>
                  <option value="Technology">Technology</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Education">Education</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Annual Revenue</Label>
                <select
                  value={applicationForm.annual_revenue}
                  onChange={(e) => setApplicationForm({...applicationForm, annual_revenue: e.target.value})}
                  className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white"
                  data-testid="app-revenue"
                >
                  <option value="">Select Range</option>
                  <option value="Under $50,000">Under $50,000</option>
                  <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                  <option value="$100,000 - $500,000">$100,000 - $500,000</option>
                  <option value="$500,000 - $1,000,000">$500,000 - $1M</option>
                  <option value="Over $1,000,000">Over $1M</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Website (optional)</Label>
              <Input 
                value={applicationForm.website}
                onChange={(e) => setApplicationForm({...applicationForm, website: e.target.value})}
                placeholder="https://yourbusiness.com"
                className="input-dark"
                data-testid="app-website"
              />
            </div>

            <div className="space-y-2">
              <Label>Business Description * (min 20 characters)</Label>
              <textarea
                value={applicationForm.description}
                onChange={(e) => setApplicationForm({...applicationForm, description: e.target.value})}
                placeholder="Describe your business, products/services, and why investors should be interested..."
                className="w-full h-24 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white resize-none"
                data-testid="app-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Investment Amount Seeking * (min $100)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input 
                  type="number"
                  value={applicationForm.investment_amount}
                  onChange={(e) => setApplicationForm({...applicationForm, investment_amount: e.target.value})}
                  placeholder="5000"
                  className="input-dark pl-8"
                  min="100"
                  data-testid="app-investment"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowApplicationModal(false); navigate('/dashboard'); }}
              className="btn-secondary"
            >
              Skip for Now
            </Button>
            <Button 
              onClick={handleSubmitApplication}
              disabled={submittingApplication}
              className="btn-primary"
              data-testid="submit-application-btn"
            >
              {submittingApplication ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {submittingApplication ? 'Submitting...' : 'Submit Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
